import { z } from "zod";

/**
 * Builds the zod object a collection parses PowerSync rows with, so a row that
 * omits a nullable column still parses instead of taking the collection down
 * with it.
 *
 * PowerSync hands over whatever the bucket payload holds. A row replicated
 * before a column joined the sync rules simply lacks that key, and `.nullable()`
 * rejects an absent key exactly as hard as a wrong type. The TanStack adapter
 * leaves a rejected row out of the collection, so every row replicated before
 * the column joined drops out, and the screens backed by it render as if the
 * user owns none of them.
 *
 * So every column that already admits `null` also accepts an absent key and
 * reads back as `null` — a missing column means the SQL NULL it stands for.
 * This has to happen during validation: zod runs `.transform()` afterwards,
 * which is too late to rescue the parse.
 *
 * A column that requires a value (`z.string()`, not `.nullable()`) still
 * rejects. There is nothing honest to invent for one, and a row missing it is
 * broken rather than merely stale. Declare a column the client can render
 * without as nullable.
 *
 * Date columns stay ISO-8601 strings end to end: TanStack DB re-validates the
 * post-transform draft against the input schema on every `.update(...)`, so a
 * transform to `Date` would make every update throw. Wrap with `new Date(...)`
 * where Date methods are needed.
 *
 * Chain the usual `.transform(...)` onto the result; the inferred row type is
 * unchanged:
 *
 *   export const SelectTaskSchema = syncedRow({
 *     id: z.string(),
 *     completedAt: z.string().nullable(),
 *   }).transform(nullishToNull);
 */
export function syncedRow<Shape extends z.ZodRawShape>(shape: Shape) {
  const tolerantShape: Record<string, z.ZodType> = {};

  for (const [name, column] of Object.entries(shape)) {
    tolerantShape[name] = tolerateAbsence(column as z.ZodType);
  }

  // The widening is deliberately invisible to the type system: it only ever
  // rescues a key the wire dropped, and every typed caller reads rows that
  // already parsed. Declaring it would push a phantom `| undefined` onto every
  // nullable column of every `collection.update` draft.
  //
  // A real `ZodObject` rather than one whole-object `z.preprocess`, because
  // `powerSyncCollectionOptions` only infers a collection's row type from a
  // `ZodObject`; hand it a pipe and every collection silently degrades to the
  // raw SQLite column types.
  return z.object(tolerantShape) as unknown as z.ZodObject<Shape>;
}

/**
 * `column` widened to accept a missing key, when it already accepts `null`.
 *
 * The absent key is rewritten to `null` and then handed to `column` itself,
 * rather than short-circuited around it. That distinction is load-bearing: a
 * column like `MediaKindSchema.nullable().transform((v) => v ?? "voice")`
 * declares a non-null type and leans on its own fallback, so answering `null`
 * on its behalf would break the type it promises.
 *
 * Probing with a parse rather than inspecting the schema's structure is what
 * makes that work across the wrappers actually in use — `serverEnumOrNull`,
 * `.nullish()`, and `.nullable().transform(…)` are all pipes, not `ZodNullable`.
 *
 * `z.nonoptional` is load-bearing, not decoration: a `ZodObject` decides what to
 * do with an absent key from the field schema alone, and for an optional one it
 * skips the field outright rather than parsing `undefined` through it. So a
 * `.nullish()` column would leave the key absent and the outer `z.preprocess`
 * never running. Stripping the optional makes the key required at the object
 * level, which is exactly right here: the preprocess answers for it.
 *
 * The result keeps `column`'s declared type, so a column can also apply this to
 * itself (`serverEnum` does) and stay tolerant outside `syncedRow`. Widening the
 * declared input instead is not an option — see `server-enum.ts` for what
 * `powerSyncCollectionOptions` does with a column whose input admits
 * `undefined`.
 */
export function tolerateAbsence<Column extends z.ZodType>(
  column: Column
): Column {
  if (!column.safeParse(null).success) {
    return column;
  }
  return z.preprocess(
    (value) => value ?? null,
    z.nonoptional(column)
  ) as unknown as Column;
}
